import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, List, Modal, Spin, Tag, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import { formatMoney } from '../../../shared/utils/money';
import { addRenewalToCart } from '../../../shared/components/RenewAction';
import cartService from '../../services/cartService';
import diskService from '../../services/diskService';
import { toastErr } from './common';

const { Text } = Typography;
const CYCLE = { monthly: '月', quarterly: '季', semi_annually: '半年', annually: '年' };

/**
 * 云盘套餐变更：升级（按剩余天数折算）/ 降级（续费后生效）→ 加入购物车，付款后生效。
 * 与域名邮箱套餐页同一套接口（available-upgrades + cart/add-change）。
 */
const PlanChangeModal = ({ open, subscriptionId, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        if (!open || !subscriptionId) return;
        setLoading(true);
        setData(null);
        diskService.planOptions(subscriptionId)
            .then((r) => setData(r?.data || null))
            .catch((e) => toastErr(e, t('Failed to load')))
            .finally(() => setLoading(false));
    }, [open, subscriptionId, t]);

    const change = async (productId, cycle) => {
        setBusy(productId);
        try {
            const r = await cartService.addChange({ subscription_id: subscriptionId, product_id: productId, ...(cycle ? { billing_cycle: cycle } : {}) });
            if (r?.success === false) { message.error(r.error || t('Operation failed')); return; }
            window.dispatchEvent(new CustomEvent('cart:updated'));
            navigate(userPath('/billing/cart'));
        } catch (e) {
            // cartService 抛的是响应体（无 status），apiClient 已弹过 HTTP 错误
            if (!e?.error && !e?.message) message.error(t('Operation failed'));
        } finally {
            setBusy(null);
        }
    };

    const ups = data?.available_upgrades || [];
    const downs = data?.downgrades || [];
    const cycleText = (c) => t(CYCLE[c] || c || '');

    return (
        <Modal open={open} onCancel={onClose} footer={null} title={t('Change plan')} width={520} destroyOnClose>
            <Spin spinning={loading}>
                {data?.current_product?.name && (
                    <div style={{ marginBottom: 12 }}>{t('Current plan')}：<Tag color="blue">{data.current_product.name}</Tag></div>
                )}
                {data?.renew_required ? (
                    <Alert type="warning" showIcon message={t('Plan expired — renew first')}
                        action={<Button size="small" type="primary" onClick={() => addRenewalToCart({ subscriptionId, navigate, t })}>{t('Renew now')}</Button>} />
                ) : (
                    <>
                        {!loading && ups.length === 0 && downs.length === 0 && <Empty description={t('No plans available to switch to')} />}
                        {ups.length > 0 && (
                            <List size="small" header={<Text strong>{t('Upgrade')}</Text>} dataSource={ups} renderItem={(u) => {
                                const c = (data?.free ? (u.cycles || [])[0] : null) || { billing_cycle: u.billing_cycle, price: u.price, cost: u.upgrade_cost };
                                return (
                                    <List.Item actions={[
                                        <Button key="b" type="primary" size="small" loading={busy === u.product_id} onClick={() => change(u.product_id, data?.free ? c.billing_cycle : undefined)}>{t('Add to cart')}</Button>,
                                    ]}>
                                        <List.Item.Meta title={u.name}
                                            description={`${formatMoney(c.price, c.currency || u.currency)}/${cycleText(c.billing_cycle)} · ${t('Due now: ')}${formatMoney(c.cost ?? c.price, c.currency || u.currency)}`} />
                                    </List.Item>
                                );
                            }} />
                        )}
                        {downs.length > 0 && (
                            <List size="small" header={<Text strong>{t('Downgrade')}</Text>} dataSource={downs} renderItem={(d) => (
                                <List.Item actions={[
                                    <Button key="b" size="small" loading={busy === d.product_id} onClick={() => change(d.product_id)}>{t('Add to cart')}</Button>,
                                ]}>
                                    <List.Item.Meta title={d.name}
                                        description={(
                                            <>
                                                <div>{formatMoney(d.price, d.currency)}/{cycleText(d.billing_cycle)} · {t('Takes effect at renewal')}</div>
                                                {(d.warnings || []).map((w) => <div key={w} style={{ color: '#d4380d' }}>{w}</div>)}
                                            </>
                                        )} />
                                </List.Item>
                            )} />
                        )}
                    </>
                )}
            </Spin>
        </Modal>
    );
};

export default PlanChangeModal;
